import { Check } from "lucide-react";
import Accreditations from "@/components/site/Accreditations";
import CTASection from "@/components/site/CTASection";
import SEO from "@/components/site/SEO";
import { TEAM, COMPANY } from "@/lib/site";

const STORY = [
  { year: "2022", title: "Foundations laid", body: "Luke and Frank found L F Building & Maintenance Ltd, bringing decades of on-site trade experience under one roof." },
  { year: "2023", title: "First new build", body: "The team completes its first ground-up residential new build — a 4-bed detached in Braintree." },
  { year: "2024", title: "Suffolk expansion", body: "We push into Suffolk with a full roofing division and dedicated project foreman on every site." },
  { year: "2025", title: "Growing team", body: "50+ projects delivered, a growing in-house crew, and every core trade covered without sub-contractors." },
];

const VALUES = [
  { t: "Craft-first", d: "We build like it's our own home. Every joint, every joist, every finish." },
  { t: "Straight-talking", d: "Fixed quotes. Honest programmes. No hidden extras." },
  { t: "Local", d: "We live and work in Essex and Suffolk. Our reputation walks with us." },
  { t: "Accountable", d: "Named site manager on every project. One point of contact, start to finish." },
];

export default function About() {
  return (
    <>
      <SEO
        title="About — L F Building & Maintenance Ltd"
        description="Family-run builders based on the Essex–Suffolk border since 2022. Meet the team and read the L F Building story."
        path="/about"
      />

      {/* HEADER */}
      <section className="bg-brand-ink text-white pt-28 md:pt-40 pb-20 md:pb-28 border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="https://customer-assets-v7afamib.emergentagent.net/job_lf-maintenance-site/artifacts/m3b2zpxd_WhatsApp%20Image%202026-01-30%20at%2010.21.24%20%286%29.jpeg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative container-lf grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-9 space-y-6">
            <span className="eyebrow text-brand">Our Story</span>
            <h1 className="font-display font-bold uppercase text-6xl md:text-8xl leading-[0.9]">
              Family-run.<br /><span className="text-brand">Craft-driven.</span>
            </h1>
            <p className="text-lg text-white/75 max-w-2xl">
              L F Building &amp; Maintenance Ltd has been building better homes across the East of England since 2022 —
              backed by 35+ years of combined trade experience. What started as a one-van outfit is now a growing team
              covering roofing, brickwork, joinery, plumbing and electrical under one roof.
            </p>
          </div>
        </div>
      </section>

      {/* STORY / TIMELINE */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-story">
        <div className="container-lf grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4 space-y-4">
            <span className="eyebrow">Timeline</span>
            <h2 className="font-display font-bold uppercase text-5xl leading-[0.95]">
              Decades<br />in the making.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <div className="border-l-2 border-brand pl-8 space-y-12">
              {STORY.map((s, i) => (
                <div key={s.year} className="relative" data-testid={`story-${i}`}>
                  <div className="absolute -left-[41px] top-1 w-4 h-4 bg-brand border-4 border-white ring-2 ring-brand" />
                  <div className="font-display font-bold text-brand text-2xl">{s.year}</div>
                  <div className="font-display font-bold uppercase text-2xl md:text-3xl mt-1 mb-2">{s.title}</div>
                  <p className="text-neutral-600 leading-relaxed max-w-xl">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 md:py-32 bg-brand-alt border-y border-black/10">
        <div className="container-lf">
          <div className="max-w-2xl mb-16 space-y-4">
            <span className="eyebrow">How We Work</span>
            <h2 className="font-display font-bold uppercase text-5xl md:text-6xl leading-[0.95]">Four values.<br />Every site.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <div key={v.t} className="bg-white border border-black/10 hover:border-brand p-8 transition-colors">
                <div className="font-display font-bold text-5xl text-brand">0{i + 1}</div>
                <div className="font-display font-bold uppercase text-2xl mt-4">{v.t}</div>
                <p className="text-neutral-600 text-sm mt-3 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-team">
        <div className="container-lf">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-2xl space-y-4">
              <span className="eyebrow">The Team</span>
              <h2 className="font-display font-bold uppercase text-5xl md:text-6xl leading-[0.95]">
                Meet the people on your site.
              </h2>
            </div>
            <p className="text-neutral-600 max-w-sm">
              Every project has a named site manager. You'll know who's on your job and how to reach them.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TEAM.map((m, i) => (
              <div key={i} data-testid={`team-member-${i}`} className="group">
                <div className="aspect-[3/4] overflow-hidden border border-black/10 group-hover:border-brand transition-colors bg-neutral-100">
                  <img src={m.image} alt={m.name} className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" loading="lazy" />
                </div>
                <div className="mt-4">
                  <div className="font-display font-bold uppercase text-xl">{m.name}</div>
                  <div className="text-xs text-brand uppercase tracking-widest mt-1">{m.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Accreditations variant="dark" />
      <CTASection eyebrow={`${COMPANY.established}`} title="Let's build yours next." />
    </>
  );
}
